import { useState } from "react";
import { isQuoteTimePriced, quoteTimePriceWords, type QuoteTimePrice } from "@/components/quote-time-price";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Boxes, Plus, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { FitChip, isWrongFit, type PartFitResult } from "@/components/parts-fit/fit-chip";

type ItemHit = {
    id: string;
    code: string;
    name: string;
    kind: string;
    price: string | null;
    labor_base_hours: string | null;
    /** A whole-device replacement: the engine's price, not the stored one (2026-10-29). */
    quote_time_price?: QuoteTimePrice | null;
    /** Parts fit (11/2): present when the search names a device. */
    fit?: PartFitResult;
};

type PickerTarget = { kind: "estimate"; estimateId: string } | { kind: "invoice"; invoiceId: string };

export function PricebookLinePicker(props: {
    estimateId?: string;
    invoiceId?: string;
    onAdded?: () => void;
    disabled?: boolean;
    // Optional controlled-open — lets a parent (e.g. an unbilled work row in
    // Closeout) pop the picker open. Omit both and it manages its own state.
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    // Comparative quote (#380): the option this add targets (estimate only), and
    // an optional label for the trigger button ("Add to Repair").
    optionId?: string | null;
    addLabel?: string;
    // Where these lines will land, named in the picker's own header (#1418).
    // The button says it, but the button is gone once the picker is open, and by
    // then the office is choosing an item and picking a quantity. Money going
    // somewhere nobody intended is exactly how Fort Worth ISD's quote broke, so
    // the destination is repeated at the moment of confirming, not only offered.
    destinationLabel?: string;
    // The quote's devices (#1823 S4). Picking one prices the item FOR that
    // device: size-table labor at the customer's rate, its re-test and fees.
    devices?: { id: string; label: string }[];
}) {
    const target: PickerTarget | null = props.invoiceId
        ? { kind: "invoice", invoiceId: props.invoiceId }
        : props.estimateId
          ? { kind: "estimate", estimateId: props.estimateId }
          : null;
    if (!target) {
        throw new Error("PricebookLinePicker requires either estimateId or invoiceId.");
    }
    const { onAdded, disabled, onOpenChange } = props;

    const qc = useQueryClient();
    const [openInternal, setOpenInternal] = useState(false);
    const open = props.open ?? openInternal;
    const setOpen = (v: boolean) => {
        if (onOpenChange) onOpenChange(v);
        else setOpenInternal(v);
    };
    const [search, setSearch] = useState("");
    const [picked, setPicked] = useState<ItemHit | null>(null);
    const [qty, setQty] = useState("1");
    const [afterHours, setAfterHours] = useState(false);
    const [memberCovers, setMemberCovers] = useState(false);
    // One device on the quote: it's the device. Several: the office picks
    // it FIRST, so the search can say which parts fit (parts fit, 11/2).
    const [deviceId, setDeviceId] = useState(props.devices?.length === 1 ? props.devices[0].id : "");
    // "Add anyway (asks why)": a part that doesn't fit needs a reason.
    const [anywayWhy, setAnywayWhy] = useState("");

    const itemQ = useQuery({
        queryKey: ["pb-picker", search, deviceId],
        queryFn: () =>
            api.get<{ items: ItemHit[] }>(
                `/api/pricebook/items?q=${encodeURIComponent(search)}&limit=20${deviceId ? `&assetId=${deviceId}` : ""}`,
            ),
        enabled: search.length >= 2 && !picked,
    });
    const pickedWrong = !!picked?.fit && isWrongFit(picked.fit.verdict);

    const addMut = useMutation({
        mutationFn: async () => {
            if (!picked) throw new Error("Pick a pricebook item first.");
            const quantity = Number(qty) || 1;
            const body: Record<string, unknown> = {
                item_id: picked.id,
                quantity,
                after_hours: afterHours,
                membership_covers: memberCovers,
            };
            if (deviceId) body.asset_id = deviceId;
            if (pickedWrong && anywayWhy.trim()) body.fit_override_reason = anywayWhy.trim();
            // Comparative quote (#380): land the whole unfolded quote under this option.
            if (target.kind === "estimate" && props.optionId) body.option_id = props.optionId;
            // Both targets run the same pricing engine now, so a picked item
            // unfolds its whole connected set (a repair pulls its kit, a
            // jurisdiction test pulls its city + admin fee).
            const path =
                target.kind === "estimate"
                    ? `/api/estimates/${target.estimateId}/lines/from-pricebook`
                    : `/api/invoices/${target.invoiceId}/lines/from-pricebook`;
            const r = await api.post<{
                insertedLineIds: string[];
                quote: { totals: { total: number }; lines: unknown[]; warnings?: string[] };
            }>(path, body);
            // Fees off means off (#1823): a fee this customer doesn't pay comes
            // back as a plain-words warning instead of a line. So does a test
            // the device already has (one test per device, 10/28).
            const feeNotes = (r.quote.warnings ?? []).filter((w) => /Pricing card|already has a test/.test(w));
            return { count: r.quote.lines.length, total: r.quote.totals.total, feeNotes };
        },
        onSuccess: (r) => {
            if (r.count === 0 && r.feeNotes.length > 0) toast.info(`Not added. ${r.feeNotes.join(" ")}`);
            else toast.success(`Added ${r.count} line${r.count === 1 ? "" : "s"} ($${r.total.toFixed(2)})`);
            if (r.count > 0 && r.feeNotes.length > 0) toast.info(r.feeNotes.join(" "));
            if (target.kind === "estimate") {
                qc.invalidateQueries({ queryKey: ["estimate", target.estimateId] });
            } else {
                qc.invalidateQueries({ queryKey: ["invoice", target.invoiceId] });
            }
            setOpen(false);
            setPicked(null);
            setSearch("");
            setQty("1");
            setAfterHours(false);
            setMemberCovers(false);
            setDeviceId(props.devices?.length === 1 ? props.devices[0].id : "");
            setAnywayWhy("");
            onAdded?.();
        },
        onError: (e: any) =>
            toast.error(`Add failed: ${e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown"}`),
    });

    const isInvoiceTarget = target.kind === "invoice";
    const targetNoun = isInvoiceTarget ? "invoice" : "estimate";

    if (!open) {
        return (
            <Button size="sm" variant="outline" onClick={() => setOpen(true)} disabled={disabled}>
                <Boxes className="h-4 w-4 mr-1" /> {props.addLabel ?? "Add from pricebook"}
            </Button>
        );
    }

    return (
        <Card className="border-primary/30">
            <CardHeader className="pb-3">
                <CardTitle className="text-sm flex flex-wrap items-center gap-x-2 gap-y-1">
                    <Boxes className="h-4 w-4" /> Add a pricebook item
                    {props.destinationLabel && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-bold text-foreground/80">
                            into {props.destinationLabel}
                        </span>
                    )}
                    <div className="flex-1" />
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                            setOpen(false);
                            setPicked(null);
                        }}
                    >
                        Cancel
                    </Button>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                {!picked ? (
                    <>
                        {(props.devices?.length ?? 0) > 1 && (
                            <div>
                                <Label htmlFor="pb-device-first">For device</Label>
                                <select
                                    id="pb-device-first"
                                    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                                    value={deviceId}
                                    onChange={(e) => setDeviceId(e.target.value)}
                                >
                                    <option value="">No device</option>
                                    {props.devices!.map((d) => (
                                        <option key={d.id} value={d.id}>
                                            {d.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div>
                            <Label>Search</Label>
                            <div className="relative">
                                <Search className="h-4 w-4 absolute left-2 top-2.5 text-foreground/80" />
                                <Input
                                    autoFocus
                                    className="pl-8"
                                    placeholder="Type code or name (min 2 chars)…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                        </div>
                        {search.length >= 2 && itemQ.data?.items && (
                            <div className="border rounded-md max-h-72 overflow-auto bg-background">
                                {itemQ.data.items.length === 0 ? (
                                    <p className="text-sm text-foreground/80 p-3 text-center">No matches.</p>
                                ) : (
                                    itemQ.data.items.map((it) => (
                                        <button
                                            key={it.id}
                                            type="button"
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 border-b last:border-0 flex items-center gap-2"
                                            onClick={() => setPicked(it)}
                                        >
                                            <Badge variant="outline" className="text-[10px] capitalize">
                                                {it.kind.replace("_", " ")}
                                            </Badge>
                                            <span className="font-mono text-xs">{it.code}</span>
                                            <span
                                                className={`flex-1 truncate ${it.fit && isWrongFit(it.fit.verdict) ? "text-foreground/50" : ""}`}
                                            >
                                                {it.name}
                                            </span>
                                            <FitChip fit={it.fit} />
                                            {isQuoteTimePriced(it) ? (
                                                <span
                                                    className="text-xs text-foreground/80 tabular-nums"
                                                    title="Priced at quote time for this customer"
                                                >
                                                    {quoteTimePriceWords(it.quote_time_price)}
                                                </span>
                                            ) : (
                                                it.price && (
                                                    <span className="text-xs text-foreground/80 tabular-nums">
                                                        ${Number(it.price).toFixed(2)}
                                                    </span>
                                                )
                                            )}
                                            {it.labor_base_hours && (
                                                <span className="text-xs text-foreground/80">
                                                    {Number(it.labor_base_hours)}h
                                                </span>
                                            )}
                                        </button>
                                    ))
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <div className="rounded-md border p-3 bg-muted/30 flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] capitalize">
                                {picked.kind.replace("_", " ")}
                            </Badge>
                            <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{picked.name}</div>
                                <div className="text-xs font-mono text-foreground/80">{picked.code}</div>
                            </div>
                            <FitChip fit={picked.fit} />
                            <Button variant="ghost" size="sm" onClick={() => setPicked(null)}>
                                Change
                            </Button>
                        </div>
                        {pickedWrong && (
                            <div
                                className="rounded-md border border-red-300 bg-red-50 p-3 text-sm dark:border-red-500/40 dark:bg-red-950/30"
                                data-testid="fit-anyway"
                            >
                                <div className="font-semibold text-red-900 dark:text-red-200">
                                    This part doesn&apos;t fit the device. {picked.fit!.reason}
                                </div>
                                <Label htmlFor="pb-anyway" className="mt-2 block">
                                    Add anyway — why?
                                </Label>
                                <Input
                                    id="pb-anyway"
                                    value={anywayWhy}
                                    onChange={(e) => setAnywayWhy(e.target.value)}
                                    placeholder="e.g. tech saw these exact checks inside"
                                />
                            </div>
                        )}
                        <div className="grid gap-3 md:grid-cols-3">
                            <div>
                                <Label>Quantity</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={qty}
                                    onChange={(e) => setQty(e.target.value)}
                                />
                            </div>
                            <div className="flex items-end gap-2 pb-1">
                                <Checkbox
                                    id="ah"
                                    checked={afterHours}
                                    onCheckedChange={(v: boolean | "indeterminate") => setAfterHours(v === true)}
                                />
                                <Label htmlFor="ah" className="cursor-pointer">
                                    After hours
                                </Label>
                            </div>
                            <div className="flex items-end gap-2 pb-1">
                                <Checkbox
                                    id="mc"
                                    checked={memberCovers}
                                    onCheckedChange={(v: boolean | "indeterminate") => setMemberCovers(v === true)}
                                />
                                <Label htmlFor="mc" className="cursor-pointer">
                                    Membership covers
                                </Label>
                            </div>
                        </div>
                        {(props.devices?.length ?? 0) > 0 && (
                            <div>
                                <Label htmlFor="pb-device">For device</Label>
                                <select
                                    id="pb-device"
                                    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                                    value={deviceId}
                                    onChange={(e) => setDeviceId(e.target.value)}
                                >
                                    <option value="">No device</option>
                                    {props.devices!.map((d) => (
                                        <option key={d.id} value={d.id}>
                                            {d.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <p className="text-xs text-foreground/80">
                            Pricing is computed live using the customer&apos;s billing mode + any per-customer rules.
                            The quote&apos;s labor / material / fee lines — including connected items like a
                            repair&apos;s kit or a test&apos;s city + admin fee — all land on this {targetNoun}.
                        </p>
                        <div className="flex justify-end gap-2 pt-1">
                            <Button
                                onClick={() => addMut.mutate()}
                                disabled={addMut.isPending || (pickedWrong && anywayWhy.trim().length < 3)}
                            >
                                {addMut.isPending ? (
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                ) : (
                                    <Plus className="h-4 w-4 mr-1" />
                                )}
                                {pickedWrong ? `Add anyway to ${targetNoun}` : `Add to ${targetNoun}`}
                            </Button>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
